import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { DestroyRef, inject, Injectable, Signal, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { combineLatest, map, Observable, of, ReplaySubject, take } from 'rxjs';
import { AgentStepModel } from 'src/app/models/api/agentStep.model';
import {
  ThreadActivitiesStepContent,
  ThreadActivitiesStepResult,
  ThreadActivitiesSummary,
} from 'src/app/services/httpServices/detection/aiFindingsHttpService/ai-finding-thread-response.model';
import { AiFindingsHttpService } from 'src/app/services/httpServices/detection/aiFindingsHttpService/ai-findings-http.service';

@Injectable()
export class SensitiveDataContentService {
  public isLoading: Signal<boolean>;
  public allSensitiveDataLoaded: Signal<boolean>;
  public isErrorDuringFetch: Signal<HttpErrorResponse | null>;
  public summary: Signal<ThreadActivitiesSummary | null>;
  public hasPartialFailure: Signal<boolean>;

  private _sensitiveDataContentSubject = new ReplaySubject<Record<AgentStepModel['id'], ThreadActivitiesStepResult>>();
  private _isLoading = signal(false);
  private _allSensitiveDataLoaded = signal(false);
  private _isErrorDuringFetch = signal<HttpErrorResponse | null>(null);
  private _summary = signal<ThreadActivitiesSummary | null>(null);
  private _hasPartialFailure = signal(false);
  private _mockStepIds: string[] | null = null;

  private readonly _httpClient = inject(HttpClient);

  constructor(
    private readonly _aiFindingsService: AiFindingsHttpService,
    private readonly _destroyRef: DestroyRef
  ) {
    this.isLoading = this._isLoading.asReadonly();
    this.allSensitiveDataLoaded = this._allSensitiveDataLoaded.asReadonly();
    this.isErrorDuringFetch = this._isErrorDuringFetch.asReadonly();
    this.summary = this._summary.asReadonly();
    this.hasPartialFailure = this._hasPartialFailure.asReadonly();
  }

  public observeSensitiveDataForId(id: AgentStepModel['id']): Observable<ThreadActivitiesStepResult | undefined> {
    return this._sensitiveDataContentSubject.pipe(map((results) => results[id]));
  }

  public setMockStepIds(stepIds: string[]): void {
    this._mockStepIds = stepIds;
  }

  public fetchContent(integrationId: string, threadId: string): void {
    if (this._isLoading()) {
      return;
    }
    if (this._mockStepIds) {
      this.loadMockContent(this._mockStepIds);
      return;
    }
    this._isLoading.set(true);
    this._isErrorDuringFetch.set(null);
    this._aiFindingsService
      .getThreadActivitiesSensitiveContent(integrationId, threadId)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (response) => {
          const resultsMap: Record<string, ThreadActivitiesStepResult> = {};
          for (const result of response.results) {
            resultsMap[result.stepId] = result;
          }
          this._sensitiveDataContentSubject.next(resultsMap);
          this._summary.set(response.summary);
          this._hasPartialFailure.set(response.summary.failed > 0);
          this._isLoading.set(false);
          this._allSensitiveDataLoaded.set(true);
        },
        error: (httpError: HttpErrorResponse) => {
          this._isLoading.set(false);
          this._isErrorDuringFetch.set(httpError);
        },
        complete: () => {
          this._isLoading.set(false);
        },
      });
  }

  public loadMockContent(stepIds: string[]): void {
    if (this._isLoading()) return;
    this._isLoading.set(true);

    type ContentMap = Record<string, ThreadActivitiesStepContent>;
    const rsa$ = this._httpClient
      .get<ContentMap>(
        'assets/mockData/rsaFetchContentMockData.json'
      )
      .pipe(take(1));
    const iam$ = this._httpClient
      .get<ContentMap>(
        'assets/mockData/iamDemoFetchContentMockData.json'
      )
      .pipe(take(1));

    combineLatest([rsa$, iam$])
      .pipe(take(1), takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: ([rsaData, iamData]) => {
          const merged = { ...rsaData, ...iamData };
          const resultsMap: Record<
            string,
            ThreadActivitiesStepResult
          > = {};
          for (const id of stepIds) {
            const content = merged[id];
            resultsMap[id] = content
              ? { stepId: id, content, error: null }
              : { stepId: id, content: null, error: null };
          }
          this._sensitiveDataContentSubject.next(resultsMap);
          this._summary.set({
            totalSteps: stepIds.length,
            succeeded: stepIds.length,
            failed: 0,
          });
          this._hasPartialFailure.set(false);
          this._isLoading.set(false);
          this._allSensitiveDataLoaded.set(true);
        },
        error: () => {
          this._isLoading.set(false);
        },
      });
  }
}
